<template>
  <div class="completed-panel">
    <!-- 9/24 刷新前（旧 DownloadItem）の見た目: 見出し＋未ログイン注意 → バナー 4 枚（PC 2×2 / SP 1 列） -->
    <div class="completed-panel__header">
      <h2 class="completed-panel__heading">目的に合わせて選ぶ</h2>
      <p v-if="!isAuthenticated" class="completed-panel__login-alert">
        ※メール送信、コンビニ印刷、ダウンロード、ヤギのメール便はログインが必須となります。<br />下記のいずれかのボタンを選択後、ログインしてください。
      </p>
    </div>

    <div id="completed-buttons" class="completed-panel__main">
      <CompletedAutoRtConsent
        v-if="isAutoRtConsentVisible"
        class="completed-panel__consent"
        :info="autoRtInfo"
        :has-partner-terms="hasAutoRtPartnerTerms"
        :partner-terms-html="autoRtPartnerTermsHtml"
        :agreed="isAgree"
        @update:agreed="onRtAgreeChange"
      />

      <!--
        並びは旧画面どおり 自分にメール → コンビニ → 応募先にメール → 端末に保存。
        GTM が見ている外側の id（pdf-email / pdf-email-none / quickemail / combie / pdf-download）は変えない。
      -->
      <div class="completed-panel__grid">
        <div
          :id="isEmailVerified ? 'pdf-email' : 'pdf-email-none'"
          class="completed-panel__item"
        >
          <CompletedActionCard
            :button-id="isEmailVerified ? pdfMailBtnId : 'pdf-mail-btn'"
            image-src="/static/renew2_img/mail_bnr_20231011.png"
            :lead="mailAction.lead"
            :title="mailAction.title"
            :disabled="!isOnline || !mailPdfEnabled"
            @click="onMailClick"
          />
          <p class="completed-panel__note">
            <a
              href="https://www.yagish.jp/information/pdf_mail_setting/"
              target="_blank"
              rel="nofollow"
              >※メールが受信できない場合はこちら</a
            >
          </p>
          <p v-if="!isAuthenticated" class="completed-panel__note">
            ※ログインが必要です。
          </p>
        </div>

        <div
          v-if="eprintEnabled"
          id="combie"
          ref="eprintCtaEl"
          class="completed-panel__item"
        >
          <CompletedActionCard
            button-id="pdf-eprint-btn"
            :image-src="eprintImageSrc"
            :lead="eprintLead"
            title="コンビニで今すぐ印刷"
            @click="handleEventWithLoginCheck('show-preview-4conv')"
          />
        </div>

        <div
          v-if="isQuickEmailVisible"
          id="quickemail"
          class="completed-panel__item"
        >
          <CompletedActionCard
            button-id="quick-email-btn"
            image-src="/static/renew2_img/quickemail_new_btn_20260419.png"
            :lead="quickEmailLabel"
            title="応募先にメール"
            :disabled="!isOnline || !localIsSendYagiEmailEnabled"
            @click="handleYagiEmailClick"
          />
        </div>

        <div
          v-if="isPdfDownloadButtonVisible"
          id="pdf-download"
          class="completed-panel__item"
        >
          <CompletedActionCard
            :button-id="pdfDownloadBtnId"
            image-src="/static/renew2_img/download_bnr_20231011.png"
            lead="端末に保存するメェ〜"
            title="端末に保存"
            :disabled="!isOnline || !downloadPdfEnabled"
            @click="handleEventWithLoginCheck('download-PDF')"
          />
          <p class="completed-panel__note">
            <a
              href="https://www.yagish.jp/information/2022-04-06-163828/?utm_source=blogWidget"
              target="_blank"
              rel="nofollow"
              >※できない場合はこちら</a
            >
          </p>
        </div>
      </div>

      <!-- 送付状は郵送が前提なので、封筒の宛名ラベルへつなぐ -->
      <AtenaLabelSuggest
        v-if="formatId === 'letter'"
        link-id="atena-suggest-completed-letter"
        frm="completed_letter"
        lead="送付状と一緒に郵送するなら、宛名もコンビニで印刷できます"
      />

      <!-- その他のテンプレートを作った人を、ヤギディ診断（yagidia の求人・記事への入口）へつなぐ -->
      <SuggestLinkCard
        v-if="isDigramSuggestVisible"
        link-id="digram-suggest-completed"
        :href="`/yagidigram?frm=completed_${formatId}`"
        img-src="/static/img/digram/yagidyimage01.png"
        title="ヤギディ診断で、向いている仕事がわかります"
        lead="20問に答えるだけで、あなたのタイプと活躍しやすい求人がわかります"
        cta="診断してみる"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * 完了画面の出力パネル（旧 DownloadItem.vue の置き換え）。
 * GTM のトリガーが見ている id（completed-buttons / pdf-email / pdf-email-none / combie /
 * quickemail / pdf-download / 各ボタン id）は旧実装から変えていない。
 */
import { computed, ref, toRef } from 'vue'
import CompletedActionCard from './CompletedActionCard.vue'
import CompletedAutoRtConsent from './CompletedAutoRtConsent.vue'
import AtenaLabelSuggest from '@/components/format-edit/AtenaLabelSuggest.vue'
import SuggestLinkCard from '@/components/format-edit/SuggestLinkCard.vue'
import completedDigramSuggestFormats from '@/utils/format-edit/completedDigramSuggestFormats.json'
import { useCompletedOutput } from '@/composables/format-edit/useCompletedOutput'
import { resolveCompletedDocumentLabel } from '@/utils/format-edit/completedDocumentLabel'

const props = withDefaults(
  defineProps<{
    formatId?: string
    isSendYagiEmailEnabled?: boolean
    mailPdfEnabled?: boolean
    downloadPdfEnabled?: boolean
    /** コンビニ印刷CTAを出すか。宛名L判など通常の eprint フロー非対応の用紙で false */
    eprintEnabled?: boolean
  }>(),
  {
    formatId: '',
    isSendYagiEmailEnabled: true,
    mailPdfEnabled: true,
    downloadPdfEnabled: true,
    eprintEnabled: true
  }
)

const emit = defineEmits<{
  (e: 'update:isSendYagiEmailEnabled', value: boolean): void
}>()

const eprintCtaEl = ref<HTMLElement | null>(null)

const {
  isAuthenticated,
  isEmailVerified,
  isOnline,
  isPdfDownloadButtonVisible,
  isQuickEmailVisible,
  quickEmailLabel,
  localIsSendYagiEmailEnabled,
  pdfMailBtnId,
  pdfDownloadBtnId,
  autoRtInfo,
  hasAutoRtPartnerTerms,
  autoRtPartnerTermsHtml,
  isAgree,
  handleEventWithLoginCheck,
  handleYagiEmailClick,
  requestEmailVerification,
  observeEprintCtaImpression,
  onRtAgreeChange
} = useCompletedOutput({
  formatId: toRef(props, 'formatId'),
  isSendYagiEmailEnabled: toRef(props, 'isSendYagiEmailEnabled'),
  onSendYagiEmail: () => emit('update:isSendYagiEmailEnabled', false)
})

observeEprintCtaImpression(eprintCtaEl)

/** 旧画面の前置き（「作った履歴書、…」）。書類名は書式に合わせる */
const eprintLead = computed(() =>
  props.formatId === 'atena'
    ? '作った宛名ラベル、シール紙で印刷する'
    : `作った${resolveCompletedDocumentLabel(props.formatId)}、今すぐ印刷するメェ〜！`
)

/** 宛名は用紙が違うので、コンビニ印刷の絵も宛名用（3 社）にする */
const eprintImageSrc = computed(() =>
  props.formatId === 'atena'
    ? '/static/renew2_img/atena_print_3stores.png'
    : '/static/renew2_img/print_20231024.png'
)

const isDigramSuggestVisible = computed(() =>
  completedDigramSuggestFormats.formatIds.includes(props.formatId)
)

const isAutoRtConsentVisible = computed(
  () => isAuthenticated.value && !!autoRtInfo.value.clientId
)

const mailAction = computed(() => {
  if (isEmailVerified.value) {
    return { lead: '登録したメアドに送るメェ〜', title: '自分にメール' }
  }
  if (isAuthenticated.value) {
    return {
      lead: 'PDFファイルを\n登録したメアドに送るためには',
      title: '確認メールで送る'
    }
  }
  return { lead: 'ログインしてPDFファイルを', title: 'メールで送る' }
})

function onMailClick() {
  if (isEmailVerified.value) {
    handleEventWithLoginCheck('mail-PDF')
    return
  }
  if (isAuthenticated.value) {
    requestEmailVerification()
    return
  }
  handleEventWithLoginCheck('send-email-verification')
}
</script>

<style lang="scss" scoped>
$deep-green: #004d40;
$alert-color: #f00;

/* 旧 DownloadItem の白いカード（card-header 8px 24px / card-content 16px 24px 24px） */
.completed-panel {
  padding: 8px 24px 24px;
  background: #fff;

  &__header {
    margin: 0 -24px 16px;
    padding: 0 24px 16px;
    border-bottom: 1px solid #ebeef5;
    text-align: center;
  }

  &__heading {
    margin: 0 0 10px;
    font-size: 1rem;
    font-weight: bold;
    color: $deep-green;
  }

  &__login-alert {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.7;
    color: $alert-color;
    text-align: left;
  }

  &__main {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* 同意ブロックは旧画面どおり見出しの下・バナーの上 */
  &__consent {
    padding: 0 14px;
    color: #333;
  }

  /* 旧 bnr_container: SP（599px 以下）は 1 列、それより広いと 2 列。間隔は 30px */
  &__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 30px;
  }

  &__item {
    text-align: center;
  }

  &__note {
    margin: 0;
    font-size: 0.8rem;
    line-height: 1.7;

    a {
      color: rgb(0 0 0 / 75%);
      text-decoration: none;
    }
  }

  @media (width >= 600px) {
    &__grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__consent {
      padding: 0 24px;
    }
  }
}
</style>
